import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./Reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=900&q=80";

const chapters = [
  {
    n: "01",
    title: "Craftsmanship first",
    body: "Every wall, joint, and finish is executed with precision. We treat each project like it's our own home or storefront — because reputation is built one detail at a time.",
  },
  {
    n: "02",
    title: "Reliability you can plan around",
    body: "Clear timelines, honest communication, and clean job sites. We show up, we finish, and we keep you informed at every stage of the build.",
  },
  {
    n: "03",
    title: "Commercial & residential expertise",
    body: "From high-traffic retail build-outs to full-home remodels, our team brings the same rigor and quality standards to projects of every scale.",
  },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-36 bg-brand-navy" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left copy */}
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>Who We Are</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-brand-text max-w-2xl">
                A renovation firm built on trust, detail, and durability.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
                GBuildersLLC delivers renovations that look sharp and stand the test of
                time. Led by founder Damian Gonzalez-Perez, our crew combines
                hands-on skill with a client-first approach on every job.
              </p>
            </Reveal>

            <div className="mt-14 space-y-10">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={0.05 * i}>
                  <div className="group flex gap-6 md:gap-8 border-t border-brand-hi pt-8">
                    <span className="font-heading text-4xl md:text-5xl font-extrabold text-brand-orange/25 group-hover:text-brand-orange/60 transition-colors leading-none shrink-0 w-16">
                      {c.n}
                    </span>
                    <div>
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-brand-text">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-brand-muted leading-relaxed max-w-lg">{c.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Right image */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal delay={0.1}>
              <div className="relative aspect-[3/4] overflow-hidden rounded-sm border border-brand-hi">
                <motion.img
                  style={{ y: imgY }}
                  src={ABOUT_IMG}
                  alt="GBuilders craftsman at work on an interior remodel"
                  className="absolute inset-0 w-full h-[116%] object-cover grayscale hover:grayscale-0 transition-[filter] duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="font-heading text-sm uppercase tracking-[0.2em] text-brand-orange font-bold">
                    On The Job
                  </p>
                  <p className="text-brand-text font-medium mt-1">Detail-driven since day one.</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="border border-brand-hi rounded-sm p-5 bg-brand-surface">
                  <p className="font-heading text-3xl font-extrabold text-brand-text">2</p>
                  <p className="text-sm text-brand-muted mt-1">States served — FL & VA</p>
                </div>
                <div className="border border-brand-hi rounded-sm p-5 bg-brand-surface">
                  <p className="font-heading text-3xl font-extrabold text-brand-text">100%</p>
                  <p className="text-sm text-brand-muted mt-1">Licensed & insured work</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
